import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfFont,
  PdfBrush,
  PdfColor,
  ETextAlign,
} from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { calcOrigin } from '../../core/genHelper';
import { GLabel } from '../../pdf/glabel';
import { ESymbol } from '../../pdf/draw/symbol';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/label';
import '../../pdf/draw/symbol';

export abstract class KdoMaBase implements ILayoutLabelGen, IItemCount {
  abstract isLinear(): boolean;

  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 3;
  }
  countMax(): number {
    return 50;
  }

  protected abstract getLeftLabel(opts: OptsLabel, ind: number): GLabel | null;
  protected abstract getRightLabel(opts: OptsLabel, ind: number): GLabel | null;
  protected abstract getLeftText(ind: number): string;
  protected abstract getRightText(opts: OptsLabel, ind: number): string;
  protected abstract getTopSymbol(opts: OptsLabel, ind: number): ESymbol | null;
  protected abstract getBottomSymbol(opts: OptsLabel, ind: number): ESymbol | null;

  protected getTotalCount(itemCount: number): number {
    return itemCount;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const cols = 5;
    const rows = 2;
    const itemsPerPage = cols * rows;

    const itemCount = Math.min(opts.list.length, this.countMax());
    const totalCount = this.getTotalCount(itemCount);
    const pageCount = Math.ceil(totalCount / itemsPerPage);

    const margin = PdfUnit.FromMillimeters(5);
    const { origin, size } = calcOrigin(cols, rows, margin, PdfSize.A4H);
    const textHeight = PdfUnit.FromMillimeters(15);
    const halfHeight = size.height / 2;
    const background = new PdfBrush(PdfColor.fromCmyk(0, 0, 0, 0.1));

    const iHaveText = opts.data.lang?.whoHas_IHave ?? 'I have:';
    const whoHasText = opts.data.lang?.whoHas_WhoHas ?? 'Who has?';
    const startText = opts.data.lang?.generator_Start ?? 'START';
    const endText = opts.data.lang?.generator_Finish ?? 'END';

    const allLeftTexts: string[][] = [];
    const allRightTexts: string[][] = [];

    for (let i = 0; i < totalCount; i++) {
      allLeftTexts.push([this.getLeftText(i)]);
      const rightText = this.getRightText(opts, i);
      allRightTexts.push(rightText ? [rightText] : []);
    }

    const labelHeight = halfHeight - textHeight;
    const labelSize = new PdfSize(size.width - 2 * margin, labelHeight);

    for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
      const page = pdf.addPage(true);
      const startIdx = pageIndex * itemsPerPage;
      const endIdx = Math.min(startIdx + itemsPerPage, totalCount);
      const itemsOnPage = endIdx - startIdx;

      if (pageIndex === 0) {
        page.adjustFontMulti(allLeftTexts, GUtil.baseFont, labelSize);
        page.adjustFontMulti(allRightTexts, GUtil.baseFont, labelSize);
      }

      const lastRow = Math.ceil(itemsOnPage / cols);
      for (let y = 0; y <= lastRow; y++) {
        const lineY = origin.y + y * size.height;
        page.drawLine(GUtil.cutPen, new PdfPoint(0, lineY), new PdfPoint(PdfSize.A4.height, lineY));
      }
      for (let x = 0; x <= cols; x++) {
        const lineX = origin.x + x * size.width;
        page.drawLine(GUtil.cutPen, new PdfPoint(lineX, 0), new PdfPoint(lineX, PdfSize.A4.width));
      }

      for (let x = 0; x < cols; x++) {
        for (let y = 0; y < rows; y++) {
          const ind = y * cols + x;
          if (startIdx + ind >= totalCount) continue;

          const rect = new PdfRect(
            origin.x + x * size.width,
            origin.y + y * size.height,
            size.width,
            size.height
          );
          const topRect = new PdfRect(rect.left, rect.top, rect.width, halfHeight);

          const l1 = new PdfRect(rect.left, rect.top, rect.width, textHeight);
          const i1 = new PdfRect(l1.left, l1.bottom, rect.width, halfHeight - textHeight);
          const l2 = new PdfRect(i1.left, i1.bottom, l1.width, l1.height);
          const i2 = new PdfRect(l2.left, l2.bottom, i1.width, i1.height);

          const globalInd = startIdx + ind;
          const leftLabel = this.getLeftLabel(opts, globalInd);
          const rightLabel = this.getRightLabel(opts, globalInd);
          const topSymbol = this.getTopSymbol(opts, globalInd);
          const bottomSymbol = this.getBottomSymbol(opts, globalInd);

          page.drawRectangle(null, background, topRect);

          if (leftLabel) {
            page.drawString(iHaveText, GUtil.baseFont, PdfBrush.Black, null, l1, ETextAlign.Center);
          } else {
            page.drawString(startText, GUtil.baseFont, PdfBrush.Black, null, l1, ETextAlign.Center);
          }

          if (topSymbol) {
            page.drawSymbol(topSymbol, PdfBrush.Black, i1, 48, ETextAlign.Center);
          } else if (leftLabel) {
            page.drawLabel(leftLabel, { rect: i1 });
          }

          if (rightLabel) {
            page.drawString(
              whoHasText,
              GUtil.baseFont,
              PdfBrush.Black,
              null,
              l2,
              ETextAlign.Center
            );
          } else {
            page.drawString(endText, GUtil.baseFont, PdfBrush.Black, null, l2, ETextAlign.Center);
          }

          if (bottomSymbol) {
            page.drawSymbol(bottomSymbol, PdfBrush.Black, i2, 48, ETextAlign.Center);
          } else if (rightLabel) {
            page.drawLabel(rightLabel, { rect: i2 });
          }

          page.drawRectangle(GUtil.cutPen, null, rect);
        }
      }
    }
  }
}

export class KdoMa__Okolo extends KdoMaBase {
  isLinear(): boolean {
    return false;
  }

  protected getLeftLabel(opts: OptsLabel, ind: number): GLabel | null {
    return opts.list[ind].left;
  }

  protected getRightLabel(opts: OptsLabel, ind: number): GLabel | null {
    return opts.list[(ind + 1) % opts.list.length].right;
  }

  protected getLeftText(ind: number): string {
    return '';
  }

  protected getRightText(opts: OptsLabel, ind: number): string {
    return opts.list[(ind + 1) % opts.list.length].right.getText() ?? '';
  }

  protected getTopSymbol(opts: OptsLabel, ind: number): ESymbol | null {
    return null;
  }

  protected getBottomSymbol(opts: OptsLabel, ind: number): ESymbol | null {
    return null;
  }
}

export class KdoMa__Start extends KdoMaBase {
  isLinear(): boolean {
    return true;
  }

  protected getTotalCount(itemCount: number): number {
    return itemCount + 1;
  }

  protected getLeftLabel(opts: OptsLabel, ind: number): GLabel | null {
    const itemCount = Math.min(opts.list.length, this.countMax());
    if (ind === 0) return null;
    return opts.list[ind - 1].left;
  }

  protected getRightLabel(opts: OptsLabel, ind: number): GLabel | null {
    const itemCount = Math.min(opts.list.length, this.countMax());
    if (ind === 0) return opts.list[0].right;
    if (ind < itemCount) return opts.list[ind].right;
    return null;
  }

  protected getLeftText(ind: number): string {
    return '';
  }

  protected getRightText(opts: OptsLabel, ind: number): string {
    return '';
  }

  protected getTopSymbol(opts: OptsLabel, ind: number): ESymbol | null {
    if (ind === 0) return ESymbol.ShapeRing;
    return null;
  }

  protected getBottomSymbol(opts: OptsLabel, ind: number): ESymbol | null {
    const itemCount = Math.min(opts.list.length, this.countMax());
    if (ind === itemCount) return ESymbol.ShapeSquare;
    return null;
  }
}
